import { ISensor } from "../SensorManager";
import { CyclingPowerEventData } from "../CyclingPowerEventData";
import { IBluetoothLESensor } from "./BluetoothLESensor";
import { BluetoothUtils } from "./BluetoothUtils";

class BluetoothSensorBase
{
    public constructor(public bluetoothLESensor: IBluetoothLESensor) { }

    get name(): string { return this.bluetoothLESensor.name }

    public onDisconnected: (() => void) | null = null;

    disconnect()
    {
        this.bluetoothLESensor.stop();
    }
}

export class HRBluetoothSensor extends BluetoothSensorBase implements ISensor<number>
{
    async start(notification: (hr: number) => void): Promise<void>
    {
        await this.bluetoothLESensor.start("heart_rate_measurement", data =>
        {
            const flags = data.getUint8(0);
            const hr = data.getUint8(1);
            notification(hr);
        });
    }
}

export class CyclingPowerBluetoothSensor extends BluetoothSensorBase implements ISensor<CyclingPowerEventData>
{
    previousWheelTime: number = 0;
    previousWheelRevs: number = 0;
    previousWheelDataAvailable: boolean = false;
    previousCranckTime: number = 0;
    previousCranckRevs: number = 0;
    framesSinceLastCranckDataChanged: number = -1;
    previousCranckRpm: number = 0;

    async start(callback: (cped: CyclingPowerEventData) => void): Promise<void>
    {
        await this.bluetoothLESensor.start("cycling_power_measurement", data =>
            {
                const now = new Date();
                const hours = now.getHours();
                const minutes = now.getMinutes();
                const seconds = now.getSeconds();
                const milliseconds = now.getMilliseconds();

                const formattedTime = `${hours}:${minutes}:${seconds}.${milliseconds}`;
                //console.log(formattedTime + ": " + BluetoothUtils.DataViewToString(data));

                let flags = data.getUint16(0);
                let instantaneousPower = data.getInt16(2, true);
                let pedalPowerBalance = data.getUint8(4);
                let wheelRevs = data.getUint32(5, true);
                let wheelTime = data.getUint16(9, true) / 2048;
                let cranckRevs = data.getUint16(11, true);
                let cranckTime = data.getUint16(13, true) / 1024;

                let wheelRpm = 0;
                let cranckRpm = 0;

                if (this.previousWheelDataAvailable)
                {
                    let time = this.previousWheelTime - wheelTime;
                    let revs = this.previousWheelRevs - wheelRevs;
                    wheelRpm = 2048 * 60 * revs / time;
                }

                this.previousWheelDataAvailable = true;
                this.previousWheelRevs = wheelRevs;
                this.previousWheelTime = wheelTime;

                if (cranckTime != this.previousCranckTime)
                {
                    if (this.framesSinceLastCranckDataChanged >= 0)
                    {
                        let timeCorrection = cranckTime < this.previousCranckTime ? 0x10000 : 0;
                        let revsCorrection = cranckRevs < this.previousCranckRevs ? 0x10000 : 0;
                        let time = cranckTime + timeCorrection - this.previousCranckTime;
                        let revs = cranckRevs + revsCorrection - this.previousCranckRevs;
                        cranckRpm = time == 0 ? Number.POSITIVE_INFINITY : 60 * revs / time;
                        this.previousCranckRpm = cranckRpm;
                    }

                    this.framesSinceLastCranckDataChanged = 0;
                }
                else
                {
                    if (this.framesSinceLastCranckDataChanged < 2)
                    {
                        cranckRpm = this.previousCranckRpm;
                    }

                    this.framesSinceLastCranckDataChanged++;
                }

                this.previousCranckRevs = cranckRevs;
                this.previousCranckTime = cranckTime;

                //console.log(`${this.framesSinceLastCranckDataChanged} ${this.previousCranckRevs}, ${this.previousCranckTime}, ${cranckRevs}, ${cranckTime} -> ${cranckRpm}`);

                let eventData = new CyclingPowerEventData(new Date());
                eventData.Power = instantaneousPower;
                eventData.CrankRpm = Math.round(cranckRpm);
                eventData.WheelRpm = Math.round(wheelRpm);
                callback(eventData);
            });
    }
}